import type { ArcRotateCamera } from '@babylonjs/core/Cameras/arcRotateCamera';
import { Vector3 } from '@babylonjs/core/Maths/math.vector';
import { BOARD_CENTER } from '../scenes/board/constants';
import { padMatches } from '../game/controls';
import type { FactionId } from '@archon3d/core';
import type { GameSession, SessionSnapshot } from '../game/session';
import type { BoardView } from '../scenes/board/boardView';
import type { GameTablets } from '../scenes/tablets/gameTablets';
import { createBoardPad, REPEAT_DELAY, REPEAT_INTERVAL, type BoardPad } from './boardPad';
import {
  activateFocused,
  ensureFocus,
  focusControl,
  focusedIn,
  isSlider,
  nudgeSlider,
  safeControl,
  stepFocus,
  topMessage,
  visible,
  type Message,
} from './domFocus';
import { nextInDirection } from './spatialNavigation';

/**
 * Where the gamepad's attention is: the pieces and cells, the wooden tablets, a message over the
 * board, or the sound panel in the corner. Back steps between them; a message that stops play
 * takes the pad until answered.
 */
export type PadZone = 'board' | 'tablets' | 'messages' | 'audio';

/** The radio and the two volume sliders, floating at the top right of the screen. */
const AUDIO_PANEL = '.persistent-audio';
/** Share of a slider's range one bumper press moves it. */
const SLIDER_STEP = 0.05;
/** Seconds between looks at the page for a message while the pad does nothing. */
export const MESSAGE_REFRESH = 0.25;

export interface BoardPadDeps {
  camera: ArcRotateCamera;
  view: BoardView;
  tablets: GameTablets;
  session: GameSession;
  pad?: BoardPad;
  /** Where the messages live; the document unless a test says otherwise. */
  root?: () => ParentNode;
}

/** Radians a second at full stick, and the share of the distance zoomed a second. */
const ORBIT_RATE = 2.2;
const TILT_RATE = 1.4;
const ZOOM_RATE = 0.9;
/** Metres a second the table slides under the camera at full stick, and how far it may go. */
const PAN_RATE = 0.5;
const PAN_LIMIT = 0.5;

/**
 * The side being asked right now: the one choosing its secret support card, the one answering a
 * Muster call, or else the side on turn. They differ in the middle of a turn, and the question is
 * the chooser's or the responder's to answer, never the attacker's.
 */
export function askedFaction(
  snapshot: Pick<SessionSnapshot, 'state' | 'cardClash' | 'muster'>,
): FactionId {
  const clash = snapshot.cardClash;
  if (clash?.phase === 'choose' && clash.chooser) return clash.chooser;
  return snapshot.muster?.faction ?? snapshot.state.activeFaction;
}

/**
 * Which pads the board listens to. One person, or people sharing the device: every pad. People
 * with a controller each: only the pad of the seat being asked (`askedFaction`), and none while
 * that seat holds the mouse or belongs to the AI, so a second pad cannot step the cursor during
 * somebody else's move, or answer somebody else's call.
 */
export function padsForTurn(
  snapshot: Pick<SessionSnapshot, 'devices' | 'state' | 'cardClash' | 'muster'>,
  pads: readonly (Gamepad | null)[],
): readonly (Gamepad | null)[] {
  const devices = snapshot.devices ?? {};
  if (Object.keys(devices).length === 0) return pads;
  const device = devices[askedFaction(snapshot)];
  if (!device || device === 'mouse') return [];
  return pads.filter((pad) => padMatches(pad, device));
}

const connectedPads = (): readonly (Gamepad | null)[] =>
  typeof navigator !== 'undefined' && typeof navigator.getGamepads === 'function'
    ? navigator.getGamepads()
    : [];

/**
 * A game controller on the board screen of a PC, alongside the mouse: the same board cursor the
 * mouse hovers is stepped by the stick, A picks like a click, the right stick turns the table, and
 * whatever asks a question over the board can be answered without reaching for the mouse.
 */
export function createBoardPadController(deps: BoardPadDeps) {
  const pad =
    deps.pad ?? createBoardPad(() => padsForTurn(deps.session.getSnapshot(), connectedPads()));
  const root = deps.root ?? (() => document);
  let zone: PadZone = 'board';
  let tabletKey: string | null = null;
  // A bumper held on a slider nudges it once, then repeats, like a held direction on the board.
  let clock = 0;
  /** What is over the board, as last asked of the page, and when. */
  let message: Message | null = null;
  let messageAt = -Infinity;
  let sliderHeld = 0;
  let sliderRepeatAt = 0;

  const audioPanel = (): HTMLElement | null => {
    // Absent in a test without a document, as on a page without the panel.
    if (typeof HTMLElement === 'undefined') return null;
    const panel = root().querySelector?.(AUDIO_PANEL);
    return panel instanceof HTMLElement && visible(panel) ? panel : null;
  };

  const leaveTablets = () => {
    tabletKey = null;
    deps.tablets.setFocus(null);
  };

  const enterTablets = () => {
    const gems = deps.tablets.focusable().filter((g) => g.enabled);
    if (gems.length === 0) return false;
    // Keep the gem the cursor was on if it is still there; else start on the first.
    tabletKey = gems.some((g) => g.key === tabletKey) ? tabletKey : gems[0]!.key;
    deps.tablets.setFocus(tabletKey);
    return true;
  };

  const stepTablets = (direction: { x: number; y: number }) => {
    const gems = deps.tablets.focusable();
    const from = gems.findIndex((g) => g.key === tabletKey);
    if (from < 0) return enterTablets();
    const next = nextInDirection(
      gems,
      gems[from]!,
      direction,
      (i) => i === from || !gems[i]!.enabled,
    );
    if (next === null) return false;
    tabletKey = gems[next]!.key;
    deps.tablets.setFocus(tabletKey);
    return true;
  };

  const cycle = (hasMessage: boolean) => {
    const audio = audioPanel();
    const zones: PadZone[] = [
      'board',
      'tablets',
      ...(hasMessage ? (['messages'] as const) : []),
      ...(audio ? (['audio'] as const) : []),
    ];
    const next = zones[(zones.indexOf(zone) + 1) % zones.length]!;
    if (zone === 'tablets') leaveTablets();
    if (zone === 'messages' || zone === 'audio') focusControl(null);
    zone = next;
    if (zone === 'tablets' && !enterTablets())
      zone = hasMessage ? 'messages' : audio ? 'audio' : 'board';
    if (zone === 'audio' && audio) ensureFocus(audio);
  };

  const escape = () =>
    window.dispatchEvent(new KeyboardEvent('keydown', { key: 'Escape', bubbles: true }));

  return {
    zone: () => zone,
    /** Call once per frame; `active` is false in the headset, in a duel and behind a page. */
    update(dt: number, active: boolean) {
      const frame = pad.update(dt);
      clock += dt;
      if (!active) {
        if (zone === 'tablets') leaveTablets();
        if (zone !== 'board') zone = 'board';
        return;
      }
      if (!frame.connected) return;

      const { camera } = deps;
      if (frame.orbit.x !== 0 || frame.orbit.y !== 0) {
        camera.alpha -= frame.orbit.x * ORBIT_RATE * dt;
        camera.beta -= frame.orbit.y * TILT_RATE * dt;
      }
      // On a slider in the sound panel the bumpers turn the volume, not the camera.
      const audio = zone === 'audio' ? audioPanel() : null;
      const slider = audio ? focusedIn(audio) : null;
      if (frame.zoom !== 0 && !isSlider(slider)) camera.radius *= 1 - frame.zoom * ZOOM_RATE * dt;
      if (frame.pan) {
        // Along the screen's own left and right, level with the table, and never off the table:
        // the whole rig slides, so the view keeps its turn and tilt.
        const right = camera.getDirection(Vector3.Right());
        right.y = 0;
        if (right.length() > 1e-6) {
          right.normalize();
          const target = camera.target;
          target.addInPlace(right.scale(frame.pan * PAN_RATE * dt));
          target.x = Math.min(
            BOARD_CENTER.x + PAN_LIMIT,
            Math.max(BOARD_CENTER.x - PAN_LIMIT, target.x),
          );
          target.z = Math.min(
            BOARD_CENTER.z + PAN_LIMIT,
            Math.max(BOARD_CENTER.z - PAN_LIMIT, target.z),
          );
        }
      }

      // The page is asked what lies over the board when the pad does something, and otherwise
      // four times a second: every frame it was six selector sweeps and layout reads at the
      // display's rate, for nothing (client-input:quality#10).
      const acted = frame.step || frame.select || frame.cancel || frame.menu || frame.switch;
      if (acted || clock - messageAt >= MESSAGE_REFRESH) {
        message = topMessage(root());
        messageAt = clock;
      }
      if (message?.modal && zone !== 'messages') {
        if (zone === 'tablets') leaveTablets();
        zone = 'messages';
      } else if (!message && zone === 'messages') zone = 'board';

      if (frame.menu) {
        escape();
        return;
      }
      if (frame.switch && !message?.modal) {
        cycle(message !== null);
        return;
      }

      switch (zone) {
        case 'board': {
          if (frame.step) deps.view.stepHover(frame.step);
          if (frame.select) deps.view.pickHover();
          if (frame.cancel) {
            const snapshot = deps.session.getSnapshot();
            if (snapshot.selectedPieceId !== null) deps.session.pickPiece(snapshot.selectedPieceId);
            else if (message) safeControl(message.root)?.click();
          }
          break;
        }
        case 'audio': {
          if (!audio) {
            zone = 'board';
            break;
          }
          if (frame.step) stepFocus(audio, frame.step);
          if (frame.select) activateFocused(audio);
          const turn = Math.sign(frame.zoom);
          if (turn !== 0 && isSlider(slider)) {
            if (turn !== sliderHeld) {
              nudgeSlider(slider, turn, SLIDER_STEP);
              sliderRepeatAt = clock + REPEAT_DELAY;
            } else if (clock >= sliderRepeatAt) {
              nudgeSlider(slider, turn, SLIDER_STEP);
              sliderRepeatAt = clock + REPEAT_INTERVAL;
            }
          }
          sliderHeld = turn;
          if (frame.cancel) {
            focusControl(null);
            zone = 'board';
          }
          break;
        }
        case 'tablets': {
          if (frame.step) stepTablets(frame.step);
          if (frame.select && tabletKey) deps.tablets.press(tabletKey);
          if (frame.cancel) {
            leaveTablets();
            zone = 'board';
          }
          break;
        }
        case 'messages': {
          if (!message) break;
          ensureFocus(message.root);
          if (frame.step) stepFocus(message.root, frame.step);
          if (frame.select) activateFocused(message.root, frame.step ?? undefined);
          if (frame.cancel) {
            const safe = safeControl(message.root);
            if (safe) safe.click();
            else escape();
          }
          break;
        }
      }
    },
    dispose() {
      if (zone === 'tablets') leaveTablets();
      if (zone === 'messages') focusControl(null);
    },
  };
}

export type BoardPadController = ReturnType<typeof createBoardPadController>;
